import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

type ReportAction = {
  id: number;
  titulo: string;
  motivo: string;
  impacto: string;
  responsable: string | null;
  fecha_limite: string | null;
  estado: "pendiente" | "en_progreso" | "completada" | "descartada";
};

type ReportData = {
  periodo: { clave: string; etiqueta: string; desde: string; hasta: string };
  economico: {
    primaBruta: number;
    primaNeta: number;
    comisiones: number;
    gastosGestion: number;
    ingresosCircuitCover: number;
    totalRecaudado: number;
    seguros: number;
    pilotos: number;
    variacionMesAnterior: Record<string, number | null>;
    variacionInteranual: Record<string, number | null>;
    acumuladoAnual: { ingresosCircuitCover: number; primaBruta: number };
    variacionAcumuladoInteranual: Record<string, number | null>;
  };
  comercial: {
    prospectsNuevos: number;
    clientesNuevos: number;
    oportunidadesCreadas: number;
    oportunidadesGanadas: number;
    oportunidadesPerdidas: number;
    tasaExito: number | null;
    oportunidadesAbiertas: number;
    pipelineTotal: number;
    pipelinePonderado: number;
    actividades: number;
    tareasVencidas: number;
    prospectsSinActividad: number;
    cicloVentaMedioDias?: number | null;
    porOrigen: Array<{ nombre: string; total: number }>;
    conversionPorOrigen?: Array<{ nombre: string; empresas: number; prospects: number; clientes: number; conversionPct: number }>;
    porEtapa: Array<{ nombre: string; total: number; importe: number }>;
    motivosPerdida: Array<{ nombre: string; total: number }>;
  };
  cartera: { clientes_activos: number; polizas_activas: number; polizas_pendientes_importes: number };
  indicadores?: {
    clientesConProduccion: number;
    ingresoMedioPorPoliza: number;
    ingresoMedioPorCliente: number;
    concentracionTop1Pct: number;
    concentracionTop5Pct: number;
    principalCliente: string | null;
  };
  renovaciones: {
    proximos30: number;
    proximos60: number;
    proximos90: number;
    prima30?: number;
    prima60?: number;
    prima90?: number;
    comision30?: number;
    comision60?: number;
    comision90?: number;
    elegiblesMes?: number;
    renovadasMes?: number;
    tasaRenovacion?: number | null;
  };
  produccion?: {
    porProducto: Array<{ nombre: string; polizas: number; prima: number; comision: number }>;
    porAseguradora: Array<{ nombre: string; polizas: number; prima: number; comision: number }>;
  };
  cobros?: {
    pendientes: number;
    importePendiente: number;
    incidencias: number;
    importeIncidencias: number;
    pendientesMas30Dias: number;
    importeMas30Dias: number;
  };
  prevision?: {
    ingresosPonderados30: number;
    ingresosPonderados60: number;
    ingresosPonderados90: number;
  };
  evolucion: Array<{ periodo: string; etiqueta: string; primaBruta: number; ingresosCircuitCover: number; prospects: number; oportunidades: number }>;
  alertas: Array<{ nivel: "critica" | "atencion" | "positiva"; titulo: string; detalle: string }>;
  notas: string[];
};

type Report = {
  id: number;
  periodo: string;
  estado: "generando" | "generado" | "enviado" | "error";
  destinatario: string;
  generado_at: string | null;
  enviado_at: string | null;
  error: string | null;
  revision: number;
  tiene_pdf: boolean;
  tiene_excel: boolean;
  datos: ReportData | null;
  acciones: ReportAction[];
  revisiones?: Array<{
    revision: number;
    estado: string;
    destinatario: string;
    generado_at: string | null;
    enviado_at: string | null;
    archivado_at: string;
    tiene_pdf: number;
    tiene_excel: number;
  }>;
};

function money(value: number) {
  return value.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

function delta(value: number | null | undefined) {
  if (value == null) return "Sin histórico";
  return `${value > 0 ? "+" : ""}${value.toLocaleString("es-ES", { maximumFractionDigits: 1 })}%`;
}

function percentage(value: number | null | undefined) {
  if (value == null) return "Sin datos";
  return `${value.toLocaleString("es-ES", { maximumFractionDigits: 1 })}%`;
}

function monthLabel(period: string) {
  const [year, month] = period.split("-").map(Number);
  return new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(new Date(year, month - 1, 1));
}

function lastClosedMonth() {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function KpiCard({ label, value, change, note }: { label: string; value: string; change?: number | null; note?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-xl font-semibold text-gray-900">{value}</p>
      {change !== undefined && (
        <p className={`mt-1 text-xs font-medium ${change != null && change < 0 ? "text-red-600" : "text-emerald-700"}`}>
          {delta(change)} frente al mes anterior
        </p>
      )}
      {note && <p className="mt-1 text-xs text-gray-500">{note}</p>}
    </div>
  );
}

export function InformeDireccionPanel() {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [period, setPeriod] = useState(lastClosedMonth());
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmRevision, setConfirmRevision] = useState(false);

  const loadList = useCallback(async (preferredId?: number) => {
    const response = await api.get<{ periodo_recomendado: string; data: Report[] }>("/admin/informes/direccion");
    setReports(response.data.data);
    setPeriod((current) => current || response.data.periodo_recomendado);
    const nextId = preferredId ?? selectedId ?? response.data.data[0]?.id ?? null;
    setSelectedId(nextId);
    return nextId;
  }, [selectedId]);

  const loadReport = useCallback(async (id: number) => {
    const response = await api.get<Report>(`/admin/informes/direccion/${id}`);
    setReport(response.data);
    setPeriod(response.data.periodo);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadList()
      .then((id) => (id && active ? loadReport(id) : undefined))
      .catch(() => active && setMessage("No se pudo cargar el histórico de informes."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = async (value: string) => {
    const id = Number(value);
    setSelectedId(id);
    setLoading(true);
    try { await loadReport(id); } finally { setLoading(false); }
  };

  const generate = async (send = false, forceRevision = false) => {
    setActionLoading(forceRevision ? "revision" : send ? "generate-send" : "generate");
    setMessage(null);
    try {
      const response = await api.post<{ id: number }>("/admin/informes/direccion/generar", {
        periodo: period,
        enviar: send,
        forzar_revision: forceRevision,
      });
      await loadList(response.data.id);
      await loadReport(response.data.id);
      setMessage(forceRevision
        ? "Revisión corregida generada. La versión enviada se conserva en el histórico."
        : send
          ? "Informe generado y enviado correctamente."
          : "Informe generado correctamente. Revísalo antes de enviarlo.");
    } catch (error: unknown) {
      const detail = error && typeof error === "object" && "response" in error
        ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setMessage(detail || "No se pudo generar el informe.");
    } finally { setActionLoading(null); }
  };

  const send = async () => {
    if (!report) return;
    setActionLoading("send");
    try {
      await api.post(`/admin/informes/direccion/${report.id}/enviar`);
      await loadReport(report.id);
      setMessage(`Informe enviado a ${report.destinatario}.`);
    } catch { setMessage("No se pudo enviar el informe."); }
    finally { setActionLoading(null); }
  };

  const download = async (kind: "pdf" | "excel") => {
    if (!report) return;
    setActionLoading(kind);
    try {
      const response = await api.get(`/admin/informes/direccion/${report.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `informe-direccion-${report.periodo}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } finally { setActionLoading(null); }
  };

  const downloadRevision = async (revision: number, kind: "pdf" | "excel") => {
    if (!report) return;
    const loadingKey = `revision-${revision}-${kind}`;
    setActionLoading(loadingKey);
    try {
      const response = await api.get(
        `/admin/informes/direccion/${report.id}/revisiones/${revision}/${kind}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `informe-direccion-${report.periodo}-revision-${revision}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } finally { setActionLoading(null); }
  };

  const updateAction = async (action: ReportAction, status: ReportAction["estado"]) => {
    if (!report) return;
    setActionLoading(`action-${action.id}`);
    try {
      await api.patch(`/admin/informes/direccion/${report.id}/acciones/${action.id}`, { estado: status });
      await loadReport(report.id);
    } finally { setActionLoading(null); }
  };

  const data = report?.datos;
  const historyOptions = reports.map((item) => ({
    value: String(item.id),
    label: `${monthLabel(item.periodo)} · ${item.estado === "enviado" ? "Enviado" : item.estado === "generado" ? "Generado" : item.estado}`,
  }));
  const sourceData = useMemo(() => data?.comercial.porOrigen.slice(0, 8) ?? [], [data]);
  const selectedPeriodIsSent = report?.periodo === period && report.estado === "enviado";

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
        <h2 className="font-semibold text-gray-900">Informe mensual de dirección</h2>
        <p className="mt-1 text-sm text-gray-600">Cierre ejecutivo con evolución, comparativas, riesgos y próximos movimientos. El día 1 analiza el mes anterior.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-white p-4">
        <label className="text-sm text-gray-700">
          <span className="mb-1 block font-medium">Mes a analizar</span>
          <input type="month" value={period} max={new Date().toISOString().slice(0, 7)} onChange={(event) => setPeriod(event.target.value)} className="h-10 rounded-md border border-gray-300 px-3" />
        </label>
        <Button
          onClick={() => selectedPeriodIsSent ? setConfirmRevision(true) : void generate(false)}
          loading={actionLoading === "generate" || actionLoading === "revision"}
        >
          {selectedPeriodIsSent ? "Crear revisión corregida" : "Generar borrador"}
        </Button>
        <Button variant="secondary" onClick={() => generate(true)} loading={actionLoading === "generate-send"} disabled={selectedPeriodIsSent}>Generar y enviar</Button>
        {historyOptions.length > 0 && (
          <Select label="Histórico" value={selectedId ? String(selectedId) : ""} onChange={(event) => handleSelect(event.target.value)} options={historyOptions} className="min-w-64" />
        )}
      </div>

      {message && <div className={`rounded-lg border p-3 text-sm ${message.includes("correct") || message.includes("enviado") ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>{message}</div>}
      {loading && <div className="rounded-xl border bg-white p-8 text-center text-gray-500">Cargando informe…</div>}

      {!loading && !data && (
        <div className="rounded-xl border border-dashed bg-white p-10 text-center">
          <p className="font-medium text-gray-900">Todavía no hay un informe generado.</p>
          <p className="mt-1 text-sm text-gray-500">Selecciona el mes y pulsa “Generar borrador” para revisarlo antes del envío.</p>
        </div>
      )}

      {!loading && data && report && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{data.periodo.etiqueta} · Revisión {report.revision ?? 1}</h2>
              <p className="text-sm text-gray-500">Estado: <span className="font-medium">{report.estado}</span>{report.enviado_at ? ` · enviado a ${report.destinatario}` : ""}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => download("pdf")} loading={actionLoading === "pdf"} disabled={!report.tiene_pdf}>Descargar PDF</Button>
              <Button variant="secondary" onClick={() => download("excel")} loading={actionLoading === "excel"} disabled={!report.tiene_excel}>Descargar Excel</Button>
              <Button onClick={send} loading={actionLoading === "send"}>Enviar por correo</Button>
            </div>
          </div>

          {(report.revisiones?.length ?? 0) > 0 && (
            <section className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <h3 className="font-semibold text-gray-900">Versiones anteriores conservadas</h3>
              <p className="mt-1 text-sm text-gray-600">Las versiones que ya fueron enviadas permanecen disponibles como histórico.</p>
              <div className="mt-3 space-y-2">
                {report.revisiones?.map((item) => (
                  <div key={item.revision} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-100 bg-white p-3 text-sm">
                    <span>Revisión {item.revision} · {item.estado}{item.enviado_at ? ` · enviada ${new Date(item.enviado_at).toLocaleString("es-ES")}` : ""}</span>
                    <div className="flex gap-2">
                      <Button variant="secondary" disabled={!item.tiene_pdf} loading={actionLoading === `revision-${item.revision}-pdf`} onClick={() => downloadRevision(item.revision, "pdf")}>PDF anterior</Button>
                      <Button variant="secondary" disabled={!item.tiene_excel} loading={actionLoading === `revision-${item.revision}-excel`} onClick={() => downloadRevision(item.revision, "excel")}>Excel anterior</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Ingresos Circuit Cover" value={money(data.economico.ingresosCircuitCover)} change={data.economico.variacionMesAnterior.ingresosCircuitCover} note="Comisiones + gastos de gestión" />
            <KpiCard label="Prima intermediada" value={money(data.economico.primaBruta)} change={data.economico.variacionMesAnterior.primaBruta} note="Volumen gestionado, no ingreso propio" />
            <KpiCard label="Pipeline ponderado" value={money(data.comercial.pipelinePonderado)} note={`${data.comercial.oportunidadesAbiertas} oportunidades abiertas`} />
            <KpiCard label="Tasa de éxito" value={data.comercial.tasaExito == null ? "Sin cierres" : `${data.comercial.tasaExito.toLocaleString("es-ES")}%`} note={`${data.comercial.oportunidadesGanadas} ganadas · ${data.comercial.oportunidadesPerdidas} perdidas`} />
            <KpiCard label="Nuevos prospects" value={String(data.comercial.prospectsNuevos)} note={`${data.comercial.clientesNuevos} clientes dados de alta`} />
            <KpiCard label="Actividad comercial" value={String(data.comercial.actividades)} note={`${data.comercial.tareasVencidas} tareas vencidas`} />
            <KpiCard label="Pólizas activas" value={String(data.cartera.polizas_activas)} note={`${data.renovaciones.proximos30} vencen en 30 días`} />
            <KpiCard label="Ingresos acumulados" value={money(data.economico.acumuladoAnual.ingresosCircuitCover)} note={`Interanual: ${delta(data.economico.variacionAcumuladoInteranual.ingresosCircuitCover)}`} />
          </div>

          <section className="rounded-xl border bg-white p-4">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900">Indicadores económicos y de cartera</h3>
              <p className="mt-1 text-sm text-gray-500">Desglose del ingreso propio, eficiencia media y exposición de la cartera.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard label="Comisiones" value={money(data.economico.comisiones)} change={data.economico.variacionMesAnterior.comisiones} />
              <KpiCard label="Gastos de gestión" value={money(data.economico.gastosGestion)} change={data.economico.variacionMesAnterior.gastosGestion} />
              <KpiCard
                label="Ingreso medio por póliza"
                value={money(data.indicadores?.ingresoMedioPorPoliza ?? (data.economico.seguros > 0 ? data.economico.ingresosCircuitCover / data.economico.seguros : 0))}
                note={`${data.economico.seguros} pólizas con producción`}
              />
              <KpiCard
                label="Ingreso medio por cliente productivo"
                value={data.indicadores ? money(data.indicadores.ingresoMedioPorCliente) : "Disponible en el próximo cierre"}
                note={data.indicadores ? `${data.indicadores.clientesConProduccion} clientes con producción` : undefined}
              />
              {data.indicadores && <>
                <KpiCard label="Concentración cliente principal" value={percentage(data.indicadores.concentracionTop1Pct)} note={data.indicadores.principalCliente ?? "Sin producción"} />
                <KpiCard label="Concentración 5 principales" value={percentage(data.indicadores.concentracionTop5Pct)} note="Porcentaje de la prima mensual" />
              </>}
              <KpiCard
                label="Tasa de renovación"
                value={percentage(data.renovaciones.tasaRenovacion)}
                note={data.renovaciones.elegiblesMes == null ? "Disponible en el próximo cierre" : `${data.renovaciones.renovadasMes} de ${data.renovaciones.elegiblesMes} renovadas`}
              />
              <KpiCard
                label="Ciclo medio de venta"
                value={data.comercial.cicloVentaMedioDias == null ? "Sin cierres" : `${data.comercial.cicloVentaMedioDias.toLocaleString("es-ES")} días`}
                note="Desde la creación hasta ganar la oportunidad"
              />
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-2">
            <section className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold text-gray-900">Embudo comercial del mes</h3>
              <p className="mt-1 text-sm text-gray-500">Volumen registrado en cada paso del proceso comercial.</p>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[
                  ["Prospects", data.comercial.prospectsNuevos],
                  ["Oportunidades", data.comercial.oportunidadesCreadas],
                  ["Ganadas", data.comercial.oportunidadesGanadas],
                  ["Perdidas", data.comercial.oportunidadesPerdidas],
                  ["Actividades", data.comercial.actividades],
                  ["Sin seguimiento", data.comercial.prospectsSinActividad],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold text-gray-900">Renovaciones y previsión</h3>
              <p className="mt-1 text-sm text-gray-500">Horizontes acumulados desde el cierre del mes.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b text-xs uppercase text-gray-500"><tr><th className="py-2">Horizonte</th><th className="py-2">Pólizas</th><th className="py-2">Prima renovable</th><th className="py-2">Ingreso comercial previsto</th></tr></thead>
                  <tbody className="divide-y">
                    {[
                      [30, data.renovaciones.proximos30, data.renovaciones.prima30, data.prevision?.ingresosPonderados30],
                      [60, data.renovaciones.proximos60, data.renovaciones.prima60, data.prevision?.ingresosPonderados60],
                      [90, data.renovaciones.proximos90, data.renovaciones.prima90, data.prevision?.ingresosPonderados90],
                    ].map(([days, policies, premium, forecast]) => (
                      <tr key={Number(days)}><td className="py-3 font-medium">{days} días</td><td className="py-3">{policies}</td><td className="py-3">{premium == null ? "Próximo cierre" : money(Number(premium))}</td><td className="py-3">{forecast == null ? "Próximo cierre" : money(Number(forecast))}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          {data.cobros && (
            <section className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold text-gray-900">Cobros y calidad de recaudación</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <KpiCard label="Cobros pendientes" value={String(data.cobros.pendientes)} note={money(data.cobros.importePendiente)} />
                <KpiCard label="Cobros con incidencia" value={String(data.cobros.incidencias)} note={money(data.cobros.importeIncidencias)} />
                <KpiCard label="Pendientes más de 30 días" value={String(data.cobros.pendientesMas30Dias)} note={money(data.cobros.importeMas30Dias)} />
              </div>
            </section>
          )}

          <div className="rounded-xl border bg-white p-4">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900">Evolución de ingresos propios (últimos 12 meses)</h3>
              <p className="mt-1 text-sm text-gray-500">Comisiones y gastos de gestión que constituyen ingresos de Circuit Cover.</p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.evolucion} margin={{ left: 10, right: 18 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <Tooltip formatter={(value) => money(Number(value))} />
                  <Bar dataKey="ingresosCircuitCover" name="Ingresos Circuit Cover" fill="#1A3AB8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {(data.produccion || data.comercial.conversionPorOrigen) && (
            <div className="grid gap-4 xl:grid-cols-2">
              <section className="rounded-xl border bg-white p-4">
                <h3 className="font-semibold text-gray-900">Producción por producto</h3>
                <p className="mt-1 text-sm text-gray-500">Pólizas cuya vigencia comenzó durante el mes analizado.</p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b text-xs uppercase text-gray-500"><tr><th className="py-2">Producto</th><th className="py-2">Pólizas</th><th className="py-2">Prima</th><th className="py-2">Comisión</th></tr></thead>
                    <tbody className="divide-y">
                      {(data.produccion?.porProducto ?? []).map((row) => <tr key={row.nombre}><td className="py-3 font-medium">{row.nombre}</td><td className="py-3">{row.polizas}</td><td className="py-3">{money(row.prima)}</td><td className="py-3">{money(row.comision)}</td></tr>)}
                      {(data.produccion?.porProducto.length ?? 0) === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-500">Sin producción clasificada en el periodo.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="rounded-xl border bg-white p-4">
                <h3 className="font-semibold text-gray-900">Conversión por origen</h3>
                <p className="mt-1 text-sm text-gray-500">Situación actual de las empresas captadas por cada canal.</p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b text-xs uppercase text-gray-500"><tr><th className="py-2">Origen</th><th className="py-2">Empresas</th><th className="py-2">Clientes</th><th className="py-2">Conversión</th></tr></thead>
                    <tbody className="divide-y">
                      {(data.comercial.conversionPorOrigen ?? []).map((row) => <tr key={row.nombre}><td className="py-3 font-medium">{row.nombre}</td><td className="py-3">{row.empresas}</td><td className="py-3">{row.clientes}</td><td className="py-3">{percentage(row.conversionPct)}</td></tr>)}
                      {(data.comercial.conversionPorOrigen?.length ?? 0) === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-500">Disponible a partir del próximo informe.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}

          {data.produccion && data.produccion.porAseguradora.length > 0 && (
            <section className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold text-gray-900">Producción por aseguradora</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {data.produccion.porAseguradora.map((row) => (
                  <div key={row.nombre} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="font-medium text-gray-900">{row.nombre}</p>
                    <p className="mt-2 text-sm text-gray-600">{row.polizas} pólizas · {money(row.prima)} de prima</p>
                    <p className="mt-1 text-xs text-gray-500">Comisión: {money(row.comision)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-xl border bg-white p-4">
              <h3 className="mb-4 font-semibold text-gray-900">Evolución de prima intermediada</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.evolucion} margin={{ left: 10, right: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                    <Tooltip formatter={(value) => money(Number(value))} />
                    <Line type="monotone" dataKey="primaBruta" name="Prima intermediada" stroke="#16a34a" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <h3 className="mb-4 font-semibold text-gray-900">Origen de nuevas empresas</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sourceData} layout="vertical" margin={{ left: 35, right: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="nombre" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" name="Altas" fill="#1A3AB8" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold text-gray-900">Alertas y señales</h3>
              <div className="mt-3 space-y-3">
                {data.alertas.map((alert, index) => (
                  <div key={`${alert.titulo}-${index}`} className={`rounded-lg border p-3 ${alert.nivel === "critica" ? "border-red-200 bg-red-50" : alert.nivel === "atencion" ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`}>
                    <p className="font-medium text-gray-900">{alert.titulo}</p>
                    <p className="mt-1 text-sm text-gray-600">{alert.detalle}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold text-gray-900">Plan de acción</h3>
              <div className="mt-3 space-y-3">
                {report.acciones.map((action) => (
                  <div key={action.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900">{action.titulo}</p>
                        <p className="mt-1 text-sm text-gray-600">{action.motivo}</p>
                        <p className="mt-1 text-xs text-gray-500">Impacto: {action.impacto}{action.fecha_limite ? ` · Plazo: ${action.fecha_limite}` : ""}</p>
                      </div>
                      <select value={action.estado} disabled={actionLoading === `action-${action.id}`} onChange={(event) => updateAction(action, event.target.value as ReportAction["estado"])} className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
                        <option value="pendiente">Pendiente</option>
                        <option value="en_progreso">En progreso</option>
                        <option value="completada">Completada</option>
                        <option value="descartada">Descartada</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
            {data.notas.map((note) => <p key={note}>• {note}</p>)}
          </div>
        </>
      )}
      <ConfirmDialog
        isOpen={confirmRevision}
        onClose={() => setConfirmRevision(false)}
        onConfirm={() => {
          setConfirmRevision(false);
          void generate(false, true);
        }}
        title="Crear revisión corregida"
        message="Se conservará la versión enviada y se generará una nueva revisión con los datos actuales. No se enviará ningún correo automáticamente."
        confirmLabel="Crear revisión"
      />
    </div>
  );
}
